import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./rules.css";
import "../themeupdate/themeupdate.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { normalizeThemeHexColor } from "../../../functions/themeAssets";
import RulesFeatureTour, {
  markRulesTourSeen,
  shouldAutoStartRulesTour,
} from "./RulesFeatureTour";

const MAX_RULES = 6;
const MIN_RULES = 1;
const MAX_RULE_CHARS = 200;
const MAX_TITLE_CHARS = 250;
const DEFAULT_BUTTON_COLOR = "#e25569";
const DEFAULT_BUTTON_TEXT_COLOR = "#ffffff";

function parseStringList(raw) {
  if (Array.isArray(raw)) return raw.map((r) => String(r ?? ""));
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map((r) => String(r ?? ""));
    } catch {
      /* not JSON */
    }
  }
  return [];
}

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [loading, setLoading] = useState(false);
  const [subTitle, setSubTitle] = useState("");
  const [rules, setRules] = useState([""]);
  const [textColor, setTextColor] = useState("#000000");
  const [subTextColor, setSubTextColor] = useState("#000000");
  const [buttonColor, setButtonColor] = useState(DEFAULT_BUTTON_COLOR);
  const [buttonTextColor, setButtonTextColor] = useState(DEFAULT_BUTTON_TEXT_COLOR);
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (status === "loading" && !data) return undefined;
    if (!shouldAutoStartRulesTour()) return undefined;
    const t = window.setTimeout(() => setTourOpen(true), 600);
    return () => window.clearTimeout(t);
  }, [status, data]);

  const closeTour = () => {
    markRulesTourSeen();
    setTourOpen(false);
  };

  useEffect(() => {
    if (!data) return;
    setSubTitle(String(data.sub_title ?? ""));
    const loadedRules = parseStringList(data.rules);
    setRules(loadedRules.length ? loadedRules : [""]);
    setTextColor(normalizeThemeHexColor(data.landing_page_title_color) || "#000000");
    setSubTextColor(normalizeThemeHexColor(data.sub_title_color) || "#000000");
    setButtonColor(normalizeThemeHexColor(data.button_color) || DEFAULT_BUTTON_COLOR);
    setButtonTextColor(
      normalizeThemeHexColor(data.button_Textcolor) || DEFAULT_BUTTON_TEXT_COLOR
    );
  }, [data]);

  const updateRule = (index, value) => {
    setRules((prev) => prev.map((rule, i) => (i === index ? value : rule)));
  };

  const addRule = () => {
    if (rules.length >= MAX_RULES) return;
    setRules((prev) => [...prev, ""]);
  };

  const removeRule = (index) => {
    if (rules.length <= MIN_RULES) return;
    setRules((prev) => prev.filter((_, i) => i !== index));
  };

  // Same checks and order as old/admin/rules.php's #add_all_rules handler.
  const validate = () => {
    const filled = rules.map((r) => String(r ?? "").trim()).filter(Boolean);
    if (!filled.length) return { ok: false, msg: "At least 1 rule required." };
    if (!subTitle.trim()) {
      return { ok: false, msg: "The subtitle cannot be empty or contain only spaces." };
    }
    if (subTitle.length > MAX_TITLE_CHARS) {
      return { ok: false, msg: "You exceed the maximum limit of the subtitle." };
    }
    if (rules.some((r) => String(r ?? "").length > MAX_RULE_CHARS)) {
      return { ok: false, msg: `Rules must below ${MAX_RULE_CHARS} characters.` };
    }
    return {
      ok: true,
      payload: {
        rules: filled,
        landing_page_title_color: textColor,
        sub_title_color: subTextColor,
        button_color: buttonColor,
        button_Textcolor: buttonTextColor,
        sub_title: subTitle,
      },
    };
  };

  const handleNext = async () => {
    const v = validate();
    if (!v.ok) {
      Swal.fire("Error", v.msg, "error");
      return;
    }
    if (!adminToken || !currentTheme) {
      Swal.fire("Error", "Admin session or theme is missing.", "error");
      return;
    }

    setLoading(true);
    try {
      await updateThemeData({
        payload: {
          data: v.payload,
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      await Swal.fire({ title: "Data Updated", icon: "success", timer: 1000, showConfirmButton: false });
      navigate("/admin/themeupdate");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setLoading(false);
    }
  };

  if (status === "loading" && !data) {
    return (
      <div className="rules-page-new">
        <div className="themeupdate-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="rules-shell">
      <div className="back-button-holder rules-top-bar admin-tour-top-bar">
        <button type="button" className="back-button" onClick={() => navigate("/admin")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
        <button
          type="button"
          className="admin-tour-launch"
          onClick={() => setTourOpen(true)}
          aria-label="Start feature tour"
        >
          <i className="fa-solid fa-route" aria-hidden />
          Take a tour
        </button>
      </div>

      <div className="rules-main-grid">
        <section className="rules-card" data-tour="tour-theme-settings">
          <div className="rules-card-body">
            <div className="rules-field-group" data-tour="tour-custom-title">
              <label className="rules-field-label" htmlFor="rules-sub-title">
                Add Custom Title:
              </label>
              <textarea
                id="rules-sub-title"
                className="rules-repeater-textarea rules-title-textarea"
                maxLength={MAX_TITLE_CHARS}
                placeholder="Custom title"
                value={subTitle}
                onChange={(e) => setSubTitle(e.target.value)}
              />
              <p className="rules-char-count">
                {subTitle.length}/{MAX_TITLE_CHARS}
              </p>
              <p className="rules-field-hint">
                <code>Maximum character limit should be {MAX_TITLE_CHARS} characters.</code>
                <code>This title will reflect on the main game page</code>
              </p>
            </div>

            <div className="rules-field-group" data-tour="tour-custom-rules">
              <label className="rules-field-label">Add Custom Rules</label>
              <div className="rules-repeater">
                {rules.map((rule, index) => {
                  const charCount = String(rule ?? "").length;
                  return (
                    <div className="rules-repeater-row" key={`rule-${index}`}>
                      <span className="rules-repeater-index">{index + 1}</span>
                      <div className="rules-repeater-field">
                        <textarea
                          name="rules"
                          className="rules-repeater-textarea"
                          placeholder="Rules"
                          value={rule}
                          onChange={(e) => updateRule(index, e.target.value)}
                        />
                        <p
                          className={`rules-char-count${charCount > MAX_RULE_CHARS ? " is-over" : ""}`}
                        >
                          {charCount}/{MAX_RULE_CHARS}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="rules-remove-btn"
                        onClick={() => removeRule(index)}
                        disabled={rules.length <= MIN_RULES || loading}
                        aria-label="Remove rule"
                        title="Remove rule"
                      >
                        <i className="fa-solid fa-xmark" aria-hidden />
                      </button>
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                className="rules-add-btn"
                onClick={addRule}
                disabled={rules.length >= MAX_RULES || loading}
              >
                <i className="fa-solid fa-plus" aria-hidden /> Add new rule
              </button>
              <p className="rules-field-hint">
                <code>Keep {MAX_RULES} rules at the most</code>
                <code>At least {MIN_RULES} rule is required</code>
                <code>Character limit of each rule is {MAX_RULE_CHARS}</code>
              </p>
            </div>
          </div>
        </section>

        <div className="rules-stack" data-tour="tour-page-content">
          <section className="rules-card">
            <header className="rules-card-header">
              <span className="rules-card-icon">
                <i className="fa-solid fa-palette" aria-hidden />
              </span>
              <div>
                <h2 className="rules-card-title">Page Content</h2>
              </div>
            </header>

            <div className="rules-card-body">
              <div className="rules-color-grid">
                <label className="rules-color-chip">
                  <span className="rules-color-chip-swatch" style={{ background: textColor }}>
                    <input
                      type="color"
                      value={textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                    />
                  </span>
                  <span className="rules-color-chip-label">Text Color</span>
                </label>

                <label className="rules-color-chip">
                  <span className="rules-color-chip-swatch" style={{ background: subTextColor }}>
                    <input
                      type="color"
                      value={subTextColor}
                      onChange={(e) => setSubTextColor(e.target.value)}
                    />
                  </span>
                  <span className="rules-color-chip-label">Sub Text color</span>
                </label>

                <label className="rules-color-chip">
                  <span className="rules-color-chip-swatch" style={{ background: buttonColor }}>
                    <input
                      type="color"
                      value={buttonColor}
                      onChange={(e) => setButtonColor(e.target.value)}
                    />
                  </span>
                  <span className="rules-color-chip-label">Button Color</span>
                </label>

                <label className="rules-color-chip">
                  <span
                    className="rules-color-chip-swatch"
                    style={{ background: buttonTextColor }}
                  >
                    <input
                      type="color"
                      value={buttonTextColor}
                      onChange={(e) => setButtonTextColor(e.target.value)}
                    />
                  </span>
                  <span className="rules-color-chip-label">Button Text Color</span>
                </label>
              </div>

              <div className="se-button-preview-row">
                <span className="rules-color-chip-label">Preview</span>
                <span
                  className="se-button-preview"
                  style={{ background: buttonColor, color: buttonTextColor }}
                >
                  Next
                </span>
              </div>
            </div>
          </section>
        </div>
      </div>

      <div className="rules-sticky-actions" data-tour="tour-actions">
        <button
          type="button"
          className="save-and-continue"
          onClick={handleNext}
          disabled={loading}
        >
          {loading ? "Saving..." : "Next"} <i className="fa-solid fa-angle-double-right" aria-hidden />
        </button>
      </div>

      <RulesFeatureTour open={tourOpen} onClose={closeTour} />
    </div>
  );
};

export default Rules;
